import type { Category } from "@cleanbudget/contract/category";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { IconAlertCircle, IconPencil, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { moveId, OrderButtons, scrollToForm } from "@/components/order-buttons";
import { PageHeader } from "@/components/page-header";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";
import { CategoryForm } from "./-components/category-form";

const categoriesQuery = () => orpc.category.list.queryOptions();

/**
 * Producten → Categorieën (#160): the groups the shop divides the assortment into, in the shop's
 * order. A group moves with its arrows; editing opens the form under the list with its fields;
 * deleting takes its products out of it (the products stay).
 */
export const Route = createFileRoute("/_admin/product/category/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(categoriesQuery()),
	component: CategoriesPage,
});

function CategoriesPage() {
	const { t } = useTranslation("category");
	const { t: tProduct } = useTranslation("product");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const { data: categories } = useSuspenseQuery(categoriesQuery());
	const [editingId, setEditingId] = useState<string>();
	const [problem, setProblem] = useState<string>();
	const [announcement, setAnnouncement] = useState("");
	const editing = categories.find((category) => category.id === editingId);

	const show = (list: Category[]) => {
		queryClient.setQueryData(categoriesQuery().queryKey, list);
		void queryClient.invalidateQueries({ queryKey: orpc.product.get.key() });
	};
	const reorder = useMutation({
		...orpc.category.reorder.mutationOptions(),
		scope: { id: "category-order" },
		onSuccess: show,
		onError: (error) => {
			setProblem(
				apiErrorTag(error) === "CategoryOrderOutdated" ? t("error.orderOutdated") : tCommon("form.failed"),
			);
			return queryClient.invalidateQueries({ queryKey: categoriesQuery().queryKey });
		},
	});
	const remove = useMutation({ ...orpc.category.delete.mutationOptions(), onSuccess: show });

	const move = (category: Category, from: number, to: number) => {
		setProblem(undefined);
		reorder.mutate({
			ids: moveId(
				categories.map((entry) => entry.id),
				from,
				to,
			),
		});
		setAnnouncement(t("moved", { name: category.name, position: to + 1, total: categories.length }));
	};

	const columns: readonly Column<Category>[] = [
		{
			id: "position",
			header: t("column.position"),
			secondary: true,
			cell: (category) => <span className="text-muted-foreground tabular-nums">{category.position + 1}</span>,
		},
		{
			id: "name",
			header: t("column.name"),
			cell: (category) => (
				<span className="flex flex-col gap-0.5">
					<span className="font-medium">{category.name}</span>
					<span className="font-mono text-xs text-muted-foreground">
						{t("path", { slug: category.slug })}
					</span>
					<span className="text-xs text-muted-foreground md:hidden">
						{t("productCount", { count: category.productCount })}
					</span>
				</span>
			),
		},
		{
			id: "products",
			header: t("column.products"),
			secondary: true,
			align: "right",
			cell: (category) => (
				<span className="tabular-nums">{t("productCount", { count: category.productCount })}</span>
			),
		},
		{
			id: "actions",
			header: <span className="sr-only">{t("column.actions")}</span>,
			align: "right",
			cell: (category) => {
				const index = categories.indexOf(category);
				return (
					<span className="flex justify-end gap-1">
						<OrderButtons
							index={index}
							count={categories.length}
							onMove={(to) => move(category, index, to)}
							upLabel={t("moveUp", { name: category.name })}
							downLabel={t("moveDown", { name: category.name })}
						/>
						<Button
							variant="ghost"
							size="icon-sm"
							aria-label={t("edit.button", { name: category.name })}
							onClick={() => {
								setEditingId(category.id);
								scrollToForm("category-form");
							}}
						>
							<IconPencil aria-hidden />
						</Button>
						<ConfirmDialog
							trigger={
								<Button
									variant="ghost"
									size="icon-sm"
									aria-label={t("delete.button", { name: category.name })}
								>
									<IconTrash aria-hidden />
								</Button>
							}
							title={t("delete.title", { name: category.name })}
							description={t("delete.description", { count: category.productCount })}
							confirmLabel={t("delete.confirm")}
							onConfirm={async () => {
								await remove.mutateAsync({ categoryId: category.id });
								if (editingId === category.id) setEditingId(undefined);
							}}
						/>
					</span>
				);
			},
		},
	];

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/product">{tProduct("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
			/>
			{problem ? (
				<Alert variant="destructive">
					<IconAlertCircle aria-hidden />
					<AlertDescription>{problem}</AlertDescription>
				</Alert>
			) : null}
			<div className="flex flex-col gap-3">
				{categories.length > 1 ? <p className="text-sm text-muted-foreground">{t("orderHint")}</p> : null}
				<DataTable
					label={t("list")}
					columns={columns}
					rows={categories}
					rowKey={(category) => category.id}
					empty={<p className="p-6 text-center text-sm text-muted-foreground">{t("empty")}</p>}
				/>
			</div>
			<div id="category-form" className="scroll-mt-4">
				{editing ? (
					<CategoryForm key={editing.id} category={editing} onDone={() => setEditingId(undefined)} />
				) : (
					<CategoryForm />
				)}
			</div>
			<p aria-live="polite" className="sr-only">
				{announcement}
			</p>
		</div>
	);
}
